import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
import ContentLoader from 'react-content-loader';
// material-ui icons
import Edit from '@material-ui/icons/Edit';
import Erase from '@material-ui/icons/Delete';
import Search from '@material-ui/icons/Search';
// material-ui/ core components
import withStyles from '@material-ui/core/styles/withStyles';
import InputAdornment from '@material-ui/core/InputAdornment';
// core components
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import { Zoom, Tooltip } from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
// style
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
// components
import Paginator from 'components/common/Paginator';
import WithPermission from 'components/User/WithPermission';
import { RADAR } from 'routes/paths';
import ModalConfirmation from 'components/common/ModalConfirmation';
import { TableLoader } from 'components/common/TableLoader';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import RouterContext from 'helpers/routerContext';
import qs from 'helpers/queryStrings';
import CustomInput from 'components/UI/CustomInput/CustomInput';

class RadarList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      search: props.appliedFilters.q,
    };
  }

  /**
   * @description
   * Redirect to permission/create route.
   */
  handleNewRule = () => {
    const history = this.context;

    const { appliedFilters } = this.props;
    const { routePaths } = this.props;

    const urlString = qs.stringify({
      ...appliedFilters,
    });

    history.push({ pathname: routePaths.CREATE, search: urlString });
  };

  /**
   * @description
   * Redirect to permission/update route.
   *
   * @param {number} id - Id of the permissionGroup to update.
   */
  handleUpdate = id => {
    const history = this.context;
    const { routePaths } = this.props;
    history.push(formatRoute(routePaths.UPDATE, { id }));
  };

  /**
   * @description
   * Redirect to permission/read route.
   *
   * @param {number} id - Id of the permissionGroup to read.
   */
  handleRead = id => {
    const history = this.context;

    const { appliedFilters, routePaths } = this.props;

    const urlString = qs.stringify({
      ...appliedFilters,
    });

    history.push({
      pathname: formatRoute(routePaths.DETAILS, { id }),
      search: urlString,
    });
  };

  /**
   * @description
   * Description of ReactTable columns.
   */
  renderColumns = () => {
    const { classes, t } = this.props;
    return [
      {
        Header: null,
        accessor: 'id',
        sortable: false,
        filterable: false,
        maxWidth: 40,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.name')}
          </span>
        ),
        accessor: 'listName',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.group')}
          </span>
        ),
        accessor: 'group',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.value')}
          </span>
        ),
        accessor: 'value',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.description')}
          </span>
        ),
        accessor: 'description',
        sortable: false,
        filterable: false,
        minWidth: 220,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.created')}
          </span>
        ),
        accessor: 'created',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('radar.list.headers.updated')}
          </span>
        ),
        accessor: 'updated',
        sortable: false,
        filterable: false,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('permissions.list.headers.actions')}
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All permissions groups from api.
   * @returns {*} - Rows to reactable.
   */
  renderRows = edges => {
    const { classes, t, appliedFilters, routePaths } = this.props;

    const rows = edges.map(({ node }) => ({
      id: <div style={{ textAlign: 'center' }}>{node.id}</div>,
      listName: (
        <a
          className={['radarDetail', classes.btnRadar].join(' ')}
          href={`${formatRoute(routePaths.DETAILS, {
            id: node.id,
          })}?${qs.stringify({
            ...appliedFilters,
          })}`}
          onClick={e => {
            e.preventDefault();
            this.handleRead(node.id);
          }}
        >
          {node.listName}
        </a>
      ),
      group: node.group,
      value: node.value,
      description: node.description,
      created: node.createdBy ? (
        <div>
          {node.createdBy.fullname} <br /> {node.createdBy.email}
        </div>
      ) : (
        <i>{t('radar.list.emptyUpdated')}</i>
      ),
      updated: node.updatedBy ? (
        <div>
          {node.updatedBy.fullname} <br />
          {node.updatedBy.email}
        </div>
      ) : (
        <i>{t('radar.list.emptyUpdated')}</i>
      ),
      actions: (
        <div className="actions-right">
          <WithPermission allowPermissions={['change_processorlistitem']}>
            {/* use this button to update the data row */}
            <Tooltip
              placement="top"
              TransitionComponent={Zoom}
              title={
                <React.Fragment>
                  {t('common.edit')}
                  <span className={classes.arrow} />
                </React.Fragment>
              }
              classes={{
                tooltip: classes.bootstrapTooltip,
                tooltipPlacementTop: classes.bootstrapPlacementTop,
              }}
            >
              <Button
                justIcon
                round
                simple
                onClick={() => {
                  this.handleUpdate(node.id);
                }}
                color="info"
                className="update"
              >
                <Edit />
              </Button>
            </Tooltip>
          </WithPermission>
          <WithPermission allowPermissions={['delete_processorlistitem']}>
            {/* use this button to delete the data row */}
            <Tooltip
              placement="top"
              TransitionComponent={Zoom}
              title={
                <React.Fragment>
                  {t('common.erase')}
                  <span className={classes.arrow} />
                </React.Fragment>
              }
              classes={{
                tooltip: classes.bootstrapTooltip,
                tooltipPlacementTop: classes.bootstrapPlacementTop,
              }}
            >
              <Button
                justIcon
                round
                simple
                onClick={() =>
                  this.setState({ modalIsOpen: true, itemToDelete: node.id })
                }
                color="danger"
                className="delete"
              >
                <Erase />
              </Button>
            </Tooltip>
          </WithPermission>
        </div>
      ),
    }));
    return rows;
  };

  /**
   * @description
   * Render Paginator and ReactTable components.
   */
  render() {
    const { modalIsOpen, itemToDelete, search } = this.state;
    const {
      data,
      classes,
      t,
      listNames,
      listGroups,
      appliedFilters,
      filter,
      loadingData,
      fullLoading,
      numberOfRowsSelected,
      changePage,
      changeNumberOfRows,
      paginationDirection,
      deleteItem,
      errors,
      titleKey,
    } = this.props;
    const edges = loadingData ? [] : data.processorList.edges;

    const rows = this.renderRows(edges);
    const columns = this.renderColumns(edges);
    const serverErrors = errors.map(error => error.message)[0];

    return (
      <div className={classes.pageContainer}>
        <Snackbars
          place="tc"
          message={serverErrors || ''}
          open={!!serverErrors}
          color="danger"
        />
        <ModalConfirmation
          show={modalIsOpen}
          confirm={() => {
            this.setState({ modalIsOpen: false });
            deleteItem(itemToDelete);
          }}
          cancel={() => this.setState({ modalIsOpen: false })}
          title={t('common.delete.radar_list_item')}
          confirmLabel={t('common.delete.radar_list_confirm')}
          cancelLabel={t('common.delete.cancel')}
        />

        <GridContainer>
          <GridItem xs={12} sm={6}>
            <h3 className={classes.title}>{t(titleKey)}</h3>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <WithPermission allowPermissions={['add_processorlistitem']}>
                  <Button
                    color="success"
                    default
                    onClick={this.handleNewRule}
                    id="new-rule"
                  >
                    {t('radar.list.new')}
                  </Button>
                </WithPermission>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
        <div className={classes.contentContainer}>
          <GridContainer justify="space-between">
            <GridItem xs={12} md={7} lg={8}>
              <GridContainer justify="space-between">
                <GridItem xs={12} sm={6}>
                  <CustomInput
                    id="search"
                    formControlProps={{
                      className: classes.radarSearchField,
                    }}
                    inputProps={{
                      placeholder: t('radar.list.filters.search'),
                      inputProps: {
                        className: classes.searchInput,
                        size: '100%',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            justIcon
                            round
                            simple
                            onClick={() => {
                              filter({ q: search });
                            }}
                            color="primary"
                            id="search-submit"
                          >
                            <Search />
                          </Button>
                        </InputAdornment>
                      ),
                      onChange: e => {
                        const { value } = e.target;
                        this.setState({ search: value });
                      },
                      onKeyPress: e => {
                        if (e.key === 'Enter') {
                          filter({ q: search });
                        }
                      },
                      type: 'text',
                      value: search || '',
                    }}
                  />
                </GridItem>
                <GridItem xs={6} sm={3}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="nameFilter"
                      className={classes.selectLabel}
                    >
                      {t('radar.list.filters.name')}
                    </InputLabel>
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={appliedFilters.listName}
                      onChange={event =>
                        filter({ listName: event.target.value })
                      }
                      inputProps={{
                        name: 'listNameFilter',
                      }}
                      id="name-filter"
                    >
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                        }}
                        value="all"
                      >
                        {t('radar.list.filters.all')}
                      </MenuItem>
                      {listNames.map(listName => (
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={listName.name}
                          key={listName.name}
                        >
                          {listName.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={6} sm={3}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="group-filter"
                      className={classes.selectLabel}
                    >
                      {t('radar.list.filters.group')}
                    </InputLabel>
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={appliedFilters.group}
                      onChange={event => filter({ group: event.target.value })}
                      inputProps={{
                        name: 'groupFilter',
                      }}
                      id="group-filter"
                    >
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                        }}
                        value="all"
                      >
                        {t('radar.list.filters.all')}
                      </MenuItem>
                      {listGroups.map(listGroup => (
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={listGroup}
                          key={listGroup}
                        >
                          {listGroup}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem xs={12} md={5} lg={4}>
              {!fullLoading ? (
                <Paginator
                  pageInfo={data.processorList.pageInfo}
                  changePage={changePage}
                  changeNumberOfRows={changeNumberOfRows}
                  numberOfRowsSelected={numberOfRowsSelected}
                  paginationDirection={paginationDirection}
                />
              ) : (
                <ContentLoader height={67} width={327} speed={2}>
                  <path
                    // eslint-disable-next-line max-len
                    d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                    fill="#D8D8D8"
                  />
                </ContentLoader>
              )}
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              {loadingData ? (
                <TableLoader />
              ) : (
                <ReactTable
                  data={rows}
                  filterable
                  columns={columns}
                  loading={loadingData}
                  pageSize={
                    edges.length < numberOfRowsSelected
                      ? edges.length
                      : numberOfRowsSelected
                  }
                  showPagination={false}
                  className={['-striped', '-highlight', classes.tbl].join(' ')}
                  noDataText={t('common.no_data')}
                  minRows={8}
                />
              )}
            </GridItem>
          </GridContainer>
          <GridContainer justify="flex-end">
            <GridItem xs={12} sm={6}>
              {!fullLoading ? (
                <Paginator
                  pageInfo={data.processorList.pageInfo}
                  changePage={changePage}
                  changeNumberOfRows={changeNumberOfRows}
                  numberOfRowsSelected={numberOfRowsSelected}
                  paginationDirection={paginationDirection}
                />
              ) : (
                <ContentLoader height={67} width={327} speed={2}>
                  <path
                    // eslint-disable-next-line max-len
                    d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                    id="Combined-Shape"
                    fill="#D8D8D8"
                  />
                </ContentLoader>
              )}
            </GridItem>
          </GridContainer>
        </div>
      </div>
    );
  }
}

/**
 * RadarList PropTypes
 * @param data - prop processor list query's results
 * @param listNames - prop processor list names query's results
 * @param listGroups - prop processor list group query's results
 * @param appliedFilters - applied filters
 * @param loadingData - bool to show loading
 * @param fullLoading - bool to show paginator
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param paginationDirection - pagination direction
 * @param errors - errors received from server
 * @param t - prop from HOC with string translations
 * @callback changePage - function passed to Pagination component
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - function to handle when filters change
 * @callback deleteItem - function to remove item from the list
 */
RadarList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  data: PropTypes.shape({}).isRequired,
  listNames: PropTypes.instanceOf(Array).isRequired,
  listGroups: PropTypes.instanceOf(Array).isRequired,
  appliedFilters: PropTypes.instanceOf(Object).isRequired,
  filter: PropTypes.func.isRequired,
  loadingData: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  paginationDirection: PropTypes.string,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  deleteItem: PropTypes.func.isRequired,
  routePaths: PropTypes.shape({
    CREATE: PropTypes.string.isRequired,
    DETAILS: PropTypes.string.isRequired,
    UPDATE: PropTypes.string.isRequired,
  }),
  titleKey: PropTypes.string,
};

RadarList.defaultProps = {
  paginationDirection: null,
  routePaths: RADAR,
  titleKey: 'radar.list.tile',
};

// to fix: The withStyles HOC set contextTypes to a children component
const RadarListWrapper = props => <RadarList {...props} />;

export default withTranslation()(
  withStyles({ ...extendedTablesStyle, ...extendedFormsStyle })(
    RadarListWrapper,
  ),
);
